/**
 * expenseCloud.js — Firestore sync for the Expense Calculator.
 *
 * Everything (months + learned store overrides + which month is open) lives in
 * ONE owner-scoped document: expenseData/{uid}. Small enough for a single doc
 * (a year of statements is well under Firestore's 1 MB limit), which keeps sync
 * simple: one real-time listener, one debounced write.
 */
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const ref = uid => doc(db, 'expenseData', uid);

/**
 * Subscribe to the owner's expense doc. Our own optimistic writes are ignored
 * (hasPendingWrites) so we don't fight the local state. Calls
 * onData(data | null, exists). Returns an unsubscribe function.
 */
export function subscribeExpense(uid, onData) {
  return onSnapshot(
    ref(uid),
    snap => {
      if (snap.metadata.hasPendingWrites) return; // echo of our own write
      onData(snap.exists() ? snap.data() : null, snap.exists());
    },
    () => {}
  );
}

/** Write the whole expense state to the cloud. */
export function saveExpense(uid, { months, overrides, activeId, trash }) {
  return setDoc(ref(uid), {
    months: Array.isArray(months) ? months : [],
    overrides: overrides || {},
    activeId: activeId || null,
    trash: Array.isArray(trash) ? trash : [], // deleted months, restorable for 30 days
    updatedAt: serverTimestamp(),
    updatedAtMs: Date.now(),
  });
}
